import { useEffect, useMemo, useState } from "react";
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	LinearProgress,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import PreviewRoundedIcon from "@mui/icons-material/PreviewRounded";
import { GlobalState } from "context/GlobalContext";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import * as XLSX from "xlsx";
import SpreadsheetPreviewTable from "./SpreadsheetPreviewTable";

const REPORT_DOWNLOAD_ENDPOINT = "/cash-card-report/download";

const getToday = () => new Date().toISOString().split("T")[0];

const getFilenameFromHeaders = (headers) => {
	const disposition = headers?.["content-disposition"];
	if (!disposition) return "pos-reco-report";

	const utfFileName = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
	if (utfFileName) return decodeURIComponent(utfFileName);

	const fileName = disposition.match(/filename="?([^"]+)"?/i)?.[1];
	return fileName || "pos-reco-report";
};

const isPreviewableText = (mimeType, fileName) => {
	const lowerFileName = fileName.toLowerCase();
	return (
		mimeType.includes("text/") ||
		mimeType.includes("json") ||
		mimeType.includes("csv") ||
		lowerFileName.endsWith(".txt") ||
		lowerFileName.endsWith(".csv") ||
		lowerFileName.endsWith(".json")
	);
};

const isSpreadsheetFile = (mimeType, fileName) => {
	const lowerFileName = fileName.toLowerCase();
	return (
		mimeType.includes("spreadsheetml") ||
		mimeType.includes("application/vnd.ms-excel") ||
		lowerFileName.endsWith(".xlsx") ||
		lowerFileName.endsWith(".xls") ||
		lowerFileName.endsWith(".csv")
	);
};

const toBlob = (data, mimeType = "application/octet-stream") => {
	if (data instanceof Blob) return data;
	if (data instanceof ArrayBuffer) return new Blob([data], { type: mimeType });
	if (typeof data === "string") return new Blob([data], { type: mimeType });
	if (data && typeof data === "object") return new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
	return null;
};

const getPreviewLabel = (previewType) => {
	if (previewType === "pdf") return "PDF Preview";
	if (previewType === "image") return "Image Preview";
	if (previewType === "text") return "Text Preview";
	if (previewType === "spreadsheet") return "Spreadsheet Preview";
	if (previewType === "unsupported") return "Download Only";
	return "Awaiting File";
};

const Report = () => {
	const { setSnackBar, snackBar, setSnackBarOpen } = GlobalState();

	const [startDate, setStartDate] = useState(getToday());
	const [endDate, setEndDate] = useState(getToday());
	const [loading, setLoading] = useState(false);
	const [downloading, setDownloading] = useState(false);
	const [fileBlob, setFileBlob] = useState(null);
	const [fileUrl, setFileUrl] = useState("");
	const [fileName, setFileName] = useState("");
	const [fileType, setFileType] = useState("");
	const [textPreview, setTextPreview] = useState("");
	const [sheetPreview, setSheetPreview] = useState({ name: "", rows: [] });
	const [error, setError] = useState("");

	useEffect(() => {
		if (!(fileBlob instanceof Blob)) {
			setFileUrl("");
			return undefined;
		}

		const objectUrl = URL.createObjectURL(fileBlob);
		setFileUrl(objectUrl);

		return () => URL.revokeObjectURL(objectUrl);
	}, [fileBlob]);

	useEffect(() => {
		const loadTextPreview = async () => {
			if (!fileBlob || !isPreviewableText(fileType, fileName) || isSpreadsheetFile(fileType, fileName)) {
				setTextPreview("");
				return;
			}

			try {
				const text = await fileBlob.text();
				setTextPreview(text);
			} catch {
				setTextPreview("");
			}
		};

		loadTextPreview();
	}, [fileBlob, fileType, fileName]);

	useEffect(() => {
		const loadSheetPreview = async () => {
			if (!fileBlob || !isSpreadsheetFile(fileType, fileName)) {
				setSheetPreview({ name: "", rows: [] });
				return;
			}

			try {
				const buffer = await fileBlob.arrayBuffer();
				const workbook = XLSX.read(buffer, { type: "array" });
				const firstSheetName = workbook.SheetNames[0];
				const firstSheet = workbook.Sheets[firstSheetName];
				const rows = XLSX.utils.sheet_to_json(firstSheet, { header: 1, defval: "" }).slice(0, 101);
				setSheetPreview({ name: firstSheetName, rows });
			} catch {
				setSheetPreview({ name: "", rows: [] });
			}
		};

		loadSheetPreview();
	}, [fileBlob, fileType, fileName]);

	const previewType = useMemo(() => {
		if (!fileBlob) return "empty";
		if (fileType.includes("pdf") || fileName.toLowerCase().endsWith(".pdf")) return "pdf";
		if (fileType.startsWith("image/")) return "image";
		if (isSpreadsheetFile(fileType, fileName)) return "spreadsheet";
		if (isPreviewableText(fileType, fileName)) return "text";
		return "unsupported";
	}, [fileBlob, fileName, fileType]);

	const fileMeta = useMemo(() => {
		if (!fileBlob) return [];

		const sizeInKb = `${(fileBlob.size / 1024).toFixed(fileBlob.size > 1024 * 1024 ? 0 : 1)} KB`;
		return [getPreviewLabel(previewType), fileName || "Generated file", sizeInKb];
	}, [fileBlob, fileName, previewType]);

	const handleGenerateReport = async (event) => {
		event.preventDefault();

		if (!startDate || !endDate) {
			setError("Please select both start date and end date.");
			return;
		}

		if (startDate > endDate) {
			setError("Start date cannot be after end date.");
			return;
		}

		setLoading(true);
		setError("");

		try {
			const response = await pythonVmInstance.post(REPORT_DOWNLOAD_ENDPOINT, { startDate, endDate }, { responseType: "blob" });

			const blobType = response.data?.type || response.headers?.["content-type"] || "application/octet-stream";
			const normalizedBlob = toBlob(response.data, blobType);

			if (!(normalizedBlob instanceof Blob)) {
				throw new Error("Invalid file response");
			}

			setFileBlob(normalizedBlob);
			setFileType(blobType);
			setFileName(getFilenameFromHeaders(response.headers));

			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Report generated successfully",
				severity: "success",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		} catch (requestError) {
			setFileBlob(null);
			setFileName("");
			setFileType("");
			setTextPreview("");
			setSheetPreview({ name: "", rows: [] });
			setError("Unable to generate report.");
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Unable to generate report",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		} finally {
			setLoading(false);
		}
	};

	const handleDownload = () => {
		if (!fileUrl) return;

		setDownloading(true);
		try {
			const anchor = document.createElement("a");
			anchor.href = fileUrl;
			anchor.download = fileName || "pos-reco-report";
			document.body.appendChild(anchor);
			anchor.click();
			document.body.removeChild(anchor);
		} finally {
			setDownloading(false);
		}
	};

	return (
		<Box
			sx={{
				width: "100%",
				height: "100%",
				display: "flex",
				flexDirection: "column",
				p: { xs: 2, md: 4 },
				boxSizing: "border-box",
				overflow: "hidden",
				gap: 2,
			}}>
			<Box
				component="form"
				onSubmit={handleGenerateReport}
				sx={{
					border: "1px solid",
					borderColor: "divider",
					borderRadius: 2,
					bgcolor: "background.paper",
					overflow: "hidden",
				}}>
				<Box
					sx={{
						px: { xs: 2, md: 3 },
						py: 2,
						borderBottom: "1px solid",
						borderColor: "divider",
						bgcolor: "grey.50",
					}}>
					<Typography variant="subtitle1" fontWeight={700}>
						POS Reco Report
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
						Built from MOP_SUMMARY. Each row is Date, Store Code, Store Name, then Sale /
						Collection / Difference / Remarks for every MOP (AMEX, CARD, UPI, etc.).
					</Typography>
				</Box>

				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(180px, 240px)) auto" },
						alignItems: "end",
						gap: 2,
						p: { xs: 2, md: 3 },
					}}>
					<Stack spacing={0.75}>
						<Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: "uppercase" }}>
							Start Date
						</Typography>
						<TextField
							size="small"
							type="date"
							value={startDate}
							onChange={(e) => setStartDate(e.target.value)}
							required
							sx={{ "& .MuiOutlinedInput-root": { bgcolor: "grey.50", borderRadius: 1.5 } }}
						/>
					</Stack>

					<Stack spacing={0.75}>
						<Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: "uppercase" }}>
							End Date
						</Typography>
						<TextField
							size="small"
							type="date"
							value={endDate}
							onChange={(e) => setEndDate(e.target.value)}
							required
							sx={{ "& .MuiOutlinedInput-root": { bgcolor: "grey.50", borderRadius: 1.5 } }}
						/>
					</Stack>

					<Button
						type="submit"
						variant="contained"
						disabled={loading}
						startIcon={loading ? <CircularProgress color="inherit" size={18} /> : <PreviewRoundedIcon />}
						sx={{ minWidth: 140, height: 40, justifySelf: { xs: "stretch", md: "end" } }}>
						{loading ? "Generating..." : "Generate"}
					</Button>
				</Box>
			</Box>

			{error && (
				<Typography variant="body2" color="error.main" sx={{ px: 0.5 }}>
					{error}
				</Typography>
			)}

			<Box
				sx={{
					flex: 1,
					minHeight: 0,
					display: "flex",
					flexDirection: "column",
					border: "1px solid",
					borderColor: "divider",
					borderRadius: 2,
					overflow: "hidden",
					bgcolor: "background.paper",
				}}>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						flexWrap: "wrap",
						gap: 1.5,
						px: 2,
						py: 1.5,
						borderBottom: "1px solid",
						borderColor: "divider",
						bgcolor: "grey.50",
					}}>
					<Box display="flex" alignItems="center" gap={1.25} sx={{ minWidth: 0 }}>
						<Box
							sx={{
								width: 34,
								height: 34,
								borderRadius: 1.5,
								display: "grid",
								placeItems: "center",
								bgcolor: "background.paper",
								border: "1px solid",
								borderColor: "divider",
								flexShrink: 0,
							}}>
							<DescriptionRoundedIcon fontSize="small" color="action" />
						</Box>
						<Box sx={{ minWidth: 0 }}>
							<Typography variant="subtitle2" fontWeight={600}>
								File Preview
							</Typography>
							<Typography variant="caption" color="text.secondary" noWrap title={fileName || undefined}>
								{fileName || "Generate a report to load the preview"}
							</Typography>
						</Box>
					</Box>
					<Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ justifyContent: "flex-end", alignItems: "center" }}>
						{fileMeta.map((item) => (
							<Chip key={item} label={item} size="small" variant="outlined" sx={{ borderRadius: "6px" }} />
						))}
						<Button
							variant="outlined"
							size="small"
							onClick={handleDownload}
							disabled={!fileUrl || downloading}
							startIcon={downloading ? <CircularProgress color="inherit" size={16} /> : <DownloadRoundedIcon />}
							sx={{ minWidth: 120 }}>
							{downloading ? "Downloading..." : "Download"}
						</Button>
					</Stack>
				</Box>

				{loading && <LinearProgress />}

				<Box sx={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
					{previewType === "empty" && (
						<Box sx={{ flex: 1, display: "grid", placeItems: "center", p: 3, bgcolor: "grey.50" }}>
							<Box
								sx={{
									textAlign: "center",
									maxWidth: 360,
									p: 3,
									border: "1px dashed",
									borderColor: "divider",
									borderRadius: 2,
									bgcolor: "background.paper",
								}}>
								<Typography variant="body1" color="text.primary" fontWeight={600} sx={{ mb: 0.5 }}>
									No preview yet
								</Typography>
								<Typography variant="body2" color="text.secondary">
									Generate a report to inspect the output here before downloading.
								</Typography>
							</Box>
						</Box>
					)}

					{previewType === "pdf" && fileUrl && (
						<Box
							component="iframe"
							title="POS Reco Report Preview"
							src={fileUrl}
							sx={{ width: "100%", height: "100%", minHeight: 0, border: "none", display: "block" }}
						/>
					)}

					{previewType === "image" && fileUrl && (
						<Box sx={{ flex: 1, overflow: "auto", p: 2, display: "grid", placeItems: "start center", bgcolor: "grey.50" }}>
							<Box component="img" src={fileUrl} alt={fileName} sx={{ maxWidth: "100%", height: "auto", borderRadius: 2 }} />
						</Box>
					)}

					{previewType === "text" && (
						<Box sx={{ flex: 1, overflow: "auto", p: 2, bgcolor: "grey.50" }}>
							<Box
								component="pre"
								sx={{
									m: 0,
									p: 2,
									borderRadius: 2,
									border: "1px solid",
									borderColor: "divider",
									bgcolor: "background.paper",
									fontFamily: "Consolas, monospace",
									fontSize: "0.8rem",
									whiteSpace: "pre-wrap",
									wordBreak: "break-word",
									color: "text.primary",
								}}>
								{textPreview}
							</Box>
						</Box>
					)}

					{previewType === "spreadsheet" && (
						<SpreadsheetPreviewTable rows={sheetPreview.rows} sheetName={sheetPreview.name} loading={loading} />
					)}

					{previewType === "unsupported" && (
						<Box sx={{ flex: 1, display: "grid", placeItems: "center", p: 3, bgcolor: "grey.50" }}>
							<Box textAlign="center">
								<Typography variant="body2" color="text.primary" fontWeight={600}>
									Preview is not available for this file type.
								</Typography>
								<Typography variant="caption" color="text.secondary">
									Use Download to open the generated file locally.
								</Typography>
							</Box>
						</Box>
					)}
				</Box>
			</Box>
		</Box>
	);
};

export default Report;
